import { Tab, Tabs } from '@heroui/react';
import { EDifficultyType, ELightGalleryType, EOsceType } from '@quesmed/types/models';
import { type ReactNode, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import type { Picture } from '@/graphql/gql/graphql';
import { useStation } from '@/store/station';
import { formatDateFromUnix } from '@/utils/date';
import { formatSec } from '@/utils/number';
import { getPictureSrc } from '@/utils/picture';
import { PreviewBrief } from './preview-brief';
// import { PreviewPhaseBriefs } from './preview-phase-briefs';
import type { IStationSchema } from './schema';

const MetadataRow = ({ label, value }: { label: string; value: ReactNode }) => (
  <div className='flex items-start justify-between gap-2 py-1.5'>
    <span className='shrink-0 text-xs font-medium text-default-500'>{label}</span>
    <span className='min-w-0 text-right text-sm text-default-700 wrap-break-word'>{value}</span>
  </div>
);

export const StationContentPreview = () => {
  const { getValues, watch } = useFormContext<IStationSchema>();
  const [tab, setTab] = useState('candidateBrief');
  const phaseTimers = watch('phaseTimers');
  const {
    name,
    hiddenName,
    difficulty,
    osceTypeId,
    assets,
    candidateBrief,
    actorBrief,
    examinerBrief,
    explanation,
    picture,
  } = getValues();
  const station = useStation.use.station();
  const picturePreviewUrl = picture ? getPictureSrc(picture as Picture) : '';

  return (
    <div className='grid grid-cols-12 divide-x divide-default-200 h-full gap-x-3 gap-y-4 items-start'>
      <div className='col-span-4 h-full text-sm pr-2'>
        <div className='flex flex-col gap-1 divide-y divide-default-200'>
          <picture className='mb-1 block w-full'>
            {picturePreviewUrl ? (
              <>
                <source media='(max-width: 1365px)' srcSet={picturePreviewUrl} />
                <img
                  className='h-72 w-full rounded-md object-cover'
                  src={picturePreviewUrl}
                  alt={name || 'Cover'}
                />
              </>
            ) : (
              <div className='flex h-72 w-full items-center justify-center rounded-md border border-default-200 bg-default-100 text-sm text-default-400'>
                No cover image
              </div>
            )}
          </picture>

          <MetadataRow label='Station ID' value={station?.id ?? '--'} />
          <MetadataRow
            label='Created At'
            value={station?.createdAt ? formatDateFromUnix(station.createdAt) : '--'}
          />
          <MetadataRow
            label='Updated At'
            value={station?.updatedAt ? formatDateFromUnix(station.updatedAt) : '--'}
          />
          <MetadataRow label='Name' value={name ?? '--'} />
          <MetadataRow label='Hidden Name' value={hiddenName ?? '--'} />
          <MetadataRow label='Difficulty' value={difficulty ? EDifficultyType[difficulty] : '--'} />
          <MetadataRow label='OSCE Type' value={osceTypeId ? EOsceType[osceTypeId] : '--'} />
          <MetadataRow
            label='Intro phase'
            value={phaseTimers?.introPhase != null ? formatSec(phaseTimers.introPhase) : '--'}
          />
          <MetadataRow
            label='Reading phase'
            value={phaseTimers?.readingPhase != null ? formatSec(phaseTimers.readingPhase) : '--'}
          />
          <MetadataRow
            label='Station phase'
            value={phaseTimers?.stationPhase != null ? formatSec(phaseTimers.stationPhase) : '--'}
          />
          <MetadataRow
            label='Examiner phase'
            value={phaseTimers?.examinerPhase != null ? formatSec(phaseTimers.examinerPhase) : '--'}
          />
        </div>
      </div>
      <div className='col-span-8 min-w-0'>
        <div className='flex flex-col gap-2'>
          <Tabs
            fullWidth
            radius='sm'
            variant='solid'
            selectedKey={tab}
            onSelectionChange={(key) => setTab(key as string)}
            classNames={{
              panel: 'px-0 py-1 overflow-visible',
              tabContent: 'px-0',
            }}
          >
            {/* Phase briefs preview disabled — replaced by phaseTimers */}
            <Tab key='candidateBrief' title={'Candidate'}>
              <PreviewBrief
                brief={candidateBrief || ''}
                lightgalleryType={ELightGalleryType.CANDIDATE}
                assets={assets || []}
              />
            </Tab>
            <Tab key='actorBrief' title={'Actor'}>
              <PreviewBrief
                brief={actorBrief || ''}
                lightgalleryType={ELightGalleryType.ACTOR}
                assets={assets || []}
              />
            </Tab>
            <Tab key='examinerBrief' title={'Examiner'}>
              <PreviewBrief
                brief={examinerBrief || ''}
                lightgalleryType={ELightGalleryType.EXAMINER}
                assets={assets || []}
              />
            </Tab>
            <Tab key='explanation' title={'Walkthrough'}>
              <PreviewBrief
                brief={explanation || ''}
                lightgalleryType={ELightGalleryType.EXPLANATION}
                assets={assets || []}
              />
            </Tab>
          </Tabs>
        </div>
      </div>
    </div>
  );
};
